import { useRef, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import AsyncSelect from 'react-select/async';
import ConfirmDialog from "../components/ui/ConfirmDialog";
import { useSession } from "../components/SessionProvider";
import { useApi } from "../hooks/useApi";
import { parseISO, format } from 'date-fns';

export default function EditSite() {
    const { siteId } = useParams();  // Get the site ID from the URL
    const [siteData, setSiteData] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [masterList, setMasterList] = useState([]);
    const { userLogin } = useSession();
    const { apiCall } = useApi();

    const initialized = useRef(false);
    const navigate = useNavigate();
    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            apiCall(`user-fetch_user_master_list`).then(response => response.json())
                .then(data => {
                    setMasterList(data);
                    return apiCall(`fetch_site`, {
                        params: { site_id: siteId }
                    });
                }).then(response => response.json())
                .then(data => {
                    setSiteData(data);
                    if (data && data.users && data.users.length) {
                        const defaultUsers = data.users.map(user_email => ({
                            value: user_email,
                            label: user_email
                        }));
                        setSelectedUsers(defaultUsers);
                    }
                });


        }
    }, [siteId]);

    const loadUserOptions = async (searchTerm, callback) => {
        if (!searchTerm || !masterList) return callback([]);

        const term = searchTerm.toLowerCase();

        const filteredUsers = masterList.filter((user) =>
            user.user_email.toLowerCase().includes(term)

        );
        const options = filteredUsers.map(user => ({
            value: user.user_email,
            label: user.user_email
        }));
        callback(options);

    };
    const setData = (key, value) => {
        setSiteData((prevData) => ({ ...prevData, [key]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const tempsiteData = siteData;
        tempsiteData.users = selectedUsers.map(user => user.value);

        try {
            const response = await apiCall(`update_site`, {
                params: { site_id: siteId },
                method: "POST",
                body: tempsiteData,
            });
            const data = await response.json();
            if (!data.success) {
                throw new Error("Failed to add the site");
            }

            alert("Project updated successfully!");
            navigate(`/`);
        } catch (error) {
            console.error("Error adding the site:", error);
            alert("Error adding the site. Please try again.");
        }
    }

    const deleteSite = async (e) => {
        e.preventDefault();
        if (siteData.total_invoices > 0) {
            setDialogOpen(true);
            return;
        }

        try {
            const response = await apiCall(`delete_site`, { params: { site_number: siteId } });
            const data = await response.json();
            if (!data.success) {
                throw new Error(data.message);
            }

            alert("Project deleted successfully!");
            setDialogOpen(false);
            navigate("/"); // Redirect to the sites list page after deletion
        } catch (error) {
            alert("Error deleting the site:" + error);
        }
    }

    return (
        <>
            {siteData && (


                <div className="p-6">
                    <div className="flex justify-between items-center mb-10">
                        <h1 className="text-2xl font-bold uppercase">Edit Project</h1>

                        <Link to={`/`} className="btn btn-primary self-end">
                            Back
                        </Link>
                    </div>
                    <div className="text-xl font-bold">{siteData?.site_name ?? ""}</div>
                    <div className="flex gap-3">
                        <h4 className="font-bold text-md">Date Created: </h4> {format(parseISO(siteData.date_created), 'MMM-yyyy')}
                    </div>
                    <hr className="my-7" />
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <label className="inline-flex items-center mb-4">
                            <input
                                type="checkbox"
                                checked={siteData.completed == true || siteData.completed == 1}
                                onChange={(e) => setData('completed', e.target.checked)}
                                className="mr-2"
                            />
                            Completed
                        </label>
                        <input
                            type="text"
                            placeholder="Project Number"
                            value={siteData?.site_number ?? ""}
                            disabled
                            className="border p-2 rounded w-full bg-gray-100"
                            required
                        />
                        <input
                            type="text"
                            placeholder="Project Name"
                            value={siteData?.site_name ?? ""}
                            onChange={(e) => setData('site_name', e.target.value)}
                            className="border p-2 rounded w-full uppercase"
                            required
                        />
                        <input
                            type="text"
                            placeholder="Project Address"
                            value={siteData?.site_address ?? ""}
                            onChange={(e) => setData('site_address', e.target.value)}
                            className="border p-2 rounded w-full"
                        />
                        <input
                            type="text"
                            placeholder="Project City"
                            value={siteData?.site_city ?? ""}
                            onChange={(e) => setData('site_city', e.target.value)}
                            className="border p-2 rounded w-full"
                        />
                        <input
                            type="text"
                            placeholder="Project Province"
                            value={siteData?.site_province ?? ""}
                            onChange={(e) => setData('site_province', e.target.value)}
                            className="border p-2 rounded w-full"
                        />
                        <input
                            type="text"
                            placeholder="Project Postal Code"
                            value={siteData?.site_postal_code ?? ""}
                            onChange={(e) => setData('site_postal_code', e.target.value)}
                            className="border p-2 rounded w-full"
                        />

                        <div>
                            <label className="block text-sm font-medium mb-1">Assign Users</label>
                            <AsyncSelect
                                isMulti
                                loadOptions={loadUserOptions}
                                defaultOptions
                                onChange={setSelectedUsers}
                                value={selectedUsers}
                                placeholder="Search users..."
                            />
                        </div>
                        <ConfirmDialog
                            open={dialogOpen}
                            title="Deletion Issue"
                            message={"The site has invoices in the system, it can't be removed."}
                            cancelText="Cancel"
                            confirmText="Okay"
                            onConfirm={() => setDialogOpen(false)}
                            onCancel={() => setDialogOpen(false)}
                        />
                        <div className="flex items-center mb-4 justify-between">
                            <div className="flex items-center mr-4">
                                <button type="submit" className="btn btn-primary">Update Project</button>
                                <Link to={`/`}>
                                    <button type="button" className="btn btn-cancel ml-4">Cancel</button>
                                </Link>
                            </div>
                            {userLogin.isAdminManager && (
                                <button type="button" className="bg-red-500 text-white p-2 rounded cursor-pointer ml-4" onClick={deleteSite}>Delete Project</button>
                            )}
                        </div>


                    </form>
                </div>
            )}
        </>
    );
};